import React, { useContext, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiChevronDown, FiLock } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/checkout.css";

const initialForm = {
  email: "",
  country: "Hong Kong",
  firstName: "",
  lastName: "",
  address: "",
  apartment: "",
  postalCode: "",
  city: "",
};

const supportedCountries = [
  "Hong Kong", "Australia", "Austria", "Belgium", "Canada", "Denmark",
  "Finland", "France", "Germany", "Ireland", "Italy", "Japan",
  "Luxembourg", "Malaysia", "Netherlands", "New Zealand", "Norway",
  "Portugal", "Singapore", "South Korea", "Spain", "Sweden",
  "Switzerland", "Thailand", "United Kingdom", "United States",
];

const CheckOut = () => {
  const { cartItems } = useContext(CartContext);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [processing, setProcessing] = useState(false);
  const [discount, setDiscount] = useState("");
  const [discountApplied, setDiscountApplied] = useState(false);

  const lines = useMemo(
    () =>
      cartItems
        .map((item) => ({
          ...item,
          product: productData.find((product) => product.id === item.id),
        }))
        .filter((item) => item.product),
    [cartItems]
  );
  const subtotal = lines.reduce(
    (total, item) => total + item.product.price * item.quantity,
    0
  );
  const shipping = siteConfig.shipping.flatRate;
  const discountValue = discountApplied ? Math.round(subtotal * 0.1) : 0;
  const total = subtotal + shipping - discountValue;

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    if (errors[name]) setErrors((current) => ({ ...current, [name]: "" }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!/^\S+@\S+\.\S+$/.test(form.email))
      nextErrors.email = "Enter a valid email address";
    [
      "firstName",
      "lastName",
      "address",
      "postalCode",
      "city",
    ].forEach((field) => {
      if (!form[field].trim()) nextErrors[field] = "This field is required";
    });
    return nextErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validate();
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      document.getElementsByName(Object.keys(nextErrors)[0])[0]?.focus();
      return;
    }
    setProcessing(true);
    const orderNumber = `${Date.now()}${Math.floor(100000 + Math.random() * 900000)}`;
    const origin = window.location.origin;
    const params = new URLSearchParams({
      orderNo: orderNumber,
      amount: total.toFixed(2),
      currency: siteConfig.currency,
      email: form.email,
      success_url: `${origin}/payment?state=success`,
      cancel_url: `${origin}/payment?state=cancel`,
      title: lines[0].product.name,
    });
    window.location.assign(`${origin}/create_payment.php?${params.toString()}`);
  };

  const applyDiscount = () => {
    setDiscountApplied(discount.trim().toUpperCase() === "PAPER10");
  };

  if (!lines.length) {
    return (
      <main className="checkout-empty">
        <h1>Your bag is empty</h1>
        <p>Add something special before heading to checkout.</p>
        <Link className="button" to="/shop">
          Browse the collection
        </Link>
      </main>
    );
  }

  const field = (name, placeholder, extra = {}) => (
    <label className={`checkout-field${errors[name] ? " has-error" : ""}`}>
      <span className="visually-hidden">{placeholder}</span>
      <input
        name={name}
        value={form[name]}
        onChange={updateField}
        placeholder={placeholder}
        aria-invalid={Boolean(errors[name])}
        {...extra}
      />
      {errors[name] && <small>{errors[name]}</small>}
    </label>
  );

  return (
    <main className="checkout-page">
      <form className="checkout-layout" onSubmit={handleSubmit} noValidate>
        <section className="checkout-form">
          <header className="checkout-heading"><p className="eyebrow">Secure checkout</p><h1>Complete your order</h1><p>Delivery details first, then continue to secure payment.</p></header>
          <section className="checkout-section">
            <div className="checkout-section__title">
              <h2>Contact</h2>
            </div>
            {field("email", "Email address", {
              type: "email",
              autoComplete: "email",
            })}
          </section>

          <section className="checkout-section">
            <h2>Delivery</h2>
            <label className="checkout-field checkout-select">
              <span>Country / Region</span>
              <select
                name="country"
                value={form.country}
                onChange={updateField}
              >
                {supportedCountries.map((country) => <option key={country}>{country}</option>)}
              </select>
              <FiChevronDown />
            </label>
            <div className="checkout-two-columns">
              {field("firstName", "First name", { autoComplete: "given-name" })}
              {field("lastName", "Last name", { autoComplete: "family-name" })}
            </div>
            {field("address", "Address", { autoComplete: "street-address" })}
            {field("apartment", "Apartment, suite, etc. (optional)")}
            <div className="checkout-two-columns">
              {field("postalCode", "Postal code", {
                autoComplete: "postal-code",
              })}
              {field("city", "City", { autoComplete: "address-level2" })}
            </div>
          </section>

          <section className="checkout-section">
            <h2>Shipping method</h2>
            <label className="shipping-method">
              <input type="radio" name="shipping" defaultChecked />
              <span>
                <strong>Tracked delivery</strong>
                <small>{siteConfig.shipping.deliveryEstimate}</small>
              </span>
              <b>{shipping === 0 ? "Free" : formatPrice(shipping)}</b>
            </label>
          </section>

          <section className="checkout-section">
            <h2>Payment</h2>
            <p className="checkout-subcopy">
              You will continue to our secure payment page. Card details are never collected on this site.
            </p>
            <div className="payment-panel">
              <div className="payment-panel__head">
                <label>
                  <input type="radio" defaultChecked /> Secure online payment
                </label>
                <div>
                  <span>VISA</span>
                  <span>MC</span>
                  <span>AMEX</span>
                </div>
              </div>
              <div className="payment-panel__body"><p><FiLock /> Payment details are entered securely after you click Pay now.</p></div>
            </div>
          </section>

          <button
            className="checkout-submit"
            type="submit"
            disabled={processing}
          >
            {processing
              ? "Processing securely…"
              : `Pay now · ${formatPrice(total)}`}
          </button>
          <p className="checkout-terms">
            By continuing, you agree to our <Link to="/terms">terms</Link> and
            acknowledge our <Link to="/privacy">privacy policy</Link>.
          </p>
        </section>

        <aside className="checkout-summary">
          <div className="checkout-summary__inner">
            <div className="checkout-products">
              {lines.map(({ product, quantity, size, color }) => (
                <article key={`${product.id}-${size}-${color}`}>
                  <div>
                    <img
                      src={product.image}
                      alt={product.name}
                      width="140"
                      height="140"
                    />
                    <span>{quantity}</span>
                  </div>
                  <p>
                    <strong>{product.name}</strong>
                    <small>
                      {color}
                      {color && size ? " · " : ""}
                      {size}
                    </small>
                  </p>
                  <b>{formatPrice(product.price * quantity)}</b>
                </article>
              ))}
            </div>
            <div className="discount-row">
              <input
                type="text"
                value={discount}
                onChange={(event) => setDiscount(event.target.value)}
                placeholder="Discount code"
              />
              <button type="button" onClick={applyDiscount}>
                Apply
              </button>
            </div>
            {discount && !discountApplied && (
              <small className="discount-hint">
                Use PAPER10 for this front-end demo.
              </small>
            )}
            <dl>
              <div>
                <dt>Subtotal</dt>
                <dd>{formatPrice(subtotal)}</dd>
              </div>
              <div>
                <dt>Shipping</dt>
                <dd>{formatPrice(shipping)}</dd>
              </div>
              {discountApplied && (
                <div>
                  <dt>Discount · PAPER10</dt>
                  <dd>−{formatPrice(discountValue)}</dd>
                </div>
              )}
              <div className="checkout-total">
                <dt>
                  Total <small>{siteConfig.currency}</small>
                </dt>
                <dd>{formatPrice(total)}</dd>
              </div>
            </dl>
          </div>
        </aside>
      </form>
    </main>
  );
};

export default CheckOut;
